function csrfToken() {
  const tag = document.querySelector("meta[name='csrf-token']");
  return tag ? tag.content : "";
}

function appendMessage(container, text, type, links, disclaimer, personalization) {
  const message = document.createElement("div");
  message.className = `petter-ai-chat__message petter-ai-chat__message--${type}`;
  message.textContent = text;

  if (Array.isArray(links) && links.length > 0) {
    const list = document.createElement("div");
    list.className = "petter-ai-chat__links";
    links.forEach((link) => {
      const anchor = document.createElement("a");
      anchor.href = link.url;
      anchor.textContent = link.label;
      if (/^https?:\/\//.test(link.url)) {
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
      }
      list.appendChild(anchor);
    });
    message.appendChild(list);
  }

  if (disclaimer) {
    const note = document.createElement("small");
    note.textContent = disclaimer;
    message.appendChild(note);
  }

  if (personalization && personalization.enabled) {
    const context = document.createElement("small");
    const labels = Array.isArray(personalization.data_labels) ? personalization.data_labels : [];
    if (labels.length > 0) {
      context.textContent = `参照: ${labels.join("・")}`;
    } else if (personalization.selection_required) {
      context.textContent = "参照: 相談する子を選んでください";
    } else {
      context.textContent = "参照: 登録済み情報なし";
    }
    context.className = "petter-ai-chat__context";
    message.appendChild(context);
  }

  container.appendChild(message);
  container.scrollTop = container.scrollHeight;
}

function setupPetterAiChat() {
  const root = document.querySelector("[data-petter-ai-chat]");
  if (!root || root.dataset.initialized === "true") return;
  root.dataset.initialized = "true";
  const panel = root.querySelector("[data-ai-chat-panel]");
  const messages = root.querySelector("[data-ai-chat-messages]");
  const form = root.querySelector("[data-ai-chat-form]");
  const input = root.querySelector("[data-ai-chat-input]");
  const submitButton = form.querySelector("button[type=submit]");
  const sampleButtons = root.querySelectorAll("[data-ai-chat-sample]");
  const quotaStatus = root.querySelector("[data-ai-chat-quota]");
  const providerToggle = root.querySelector("[data-ai-provider-toggle]");
  const providerStatus = root.querySelector("[data-ai-provider-status]");
  const personalizationToggle = root.querySelector("[data-ai-personalization-toggle]");
  const personalizationStatus = root.querySelector("[data-ai-personalization-status]");
  const petSelect = root.querySelector("[data-ai-pet-select]");
  const providerVersion = root.dataset.providerConsentVersion;
  const personalizationVersion = root.dataset.personalizationConsentVersion;
  const pageAccountScope = root.dataset.accountScope;
  let currentQuota = null;
  let currentConsent = null;
  let historyToken = null;
  let submitting = false;
  let quotaLoading = false;
  let consentUpdating = false;
  let resetTimer = null;
  let contextEpoch = 0;
  let disposed = false;
  let accountChanged = false;
  let revalidationRequired = false;
  const active = () => !disposed && document.contains(root);
  const isSmallScreen = () => window.matchMedia("(max-width: 575.98px)").matches;
  const updateViewportHeight = () => {
    const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    document.documentElement.style.setProperty("--petter-ai-vh", `${Math.max(360, Math.round(height))}px`);
  };
  const resetConversation = () => {
    historyToken = null;
    contextEpoch += 1;
    messages.textContent = "";
  };
  const updateControls = () => {
    const busy = submitting || quotaLoading || consentUpdating || !active();
    const providerVersionChanged = currentConsent && providerVersion !== currentConsent.provider_version;
    const personalVersionChanged = currentConsent && personalizationVersion !== currentConsent.personalization_version;
    const disabled = busy || !currentQuota || !currentQuota.available || !currentConsent || !currentConsent.provider_enabled || providerVersionChanged;
    input.disabled = disabled;
    submitButton.disabled = disabled;
    sampleButtons.forEach((button) => { button.disabled = disabled; });
    if (providerToggle) providerToggle.disabled = busy || !currentConsent || (providerVersionChanged && !currentConsent.provider_enabled);
    if (personalizationToggle) personalizationToggle.disabled = busy || !currentConsent || !currentConsent.provider_enabled || (personalVersionChanged && !currentConsent.personalization_enabled);
    if (petSelect) petSelect.disabled = busy || !currentConsent || !currentConsent.personalization_enabled;
    form.setAttribute("aria-busy", submitting ? "true" : "false");
  };
  const validConsent = (consent) => consent && typeof consent.account_scope === "string" && consent.account_scope.length > 0 &&
    Number.isSafeInteger(consent.revision) && consent.revision >= 0 &&
    typeof consent.provider_enabled === "boolean" && typeof consent.personalization_enabled === "boolean" &&
    typeof consent.provider_version === "string" && typeof consent.personalization_version === "string";
  const applyConsent = (consent) => {
    if (!validConsent(consent)) throw new Error("consent unavailable");
    // Account scope is checked before revision: another account may have the same or a lower revision.
    if (consent.account_scope !== pageAccountScope) {
      resetConversation();
      input.value = "";
      if (petSelect) { petSelect.value = ""; petSelect.textContent = ""; }
      currentConsent = null;
      currentQuota = null;
      accountChanged = true;
      disposed = true;
      window.clearTimeout(resetTimer);
      updateControls();
      window.location.reload();
      return false;
    }
    if (currentConsent && consent.revision < currentConsent.revision) return false;
    if (currentConsent && (consent.revision !== currentConsent.revision ||
        consent.provider_version !== currentConsent.provider_version ||
        consent.personalization_version !== currentConsent.personalization_version ||
        consent.provider_enabled !== currentConsent.provider_enabled ||
        consent.personalization_enabled !== currentConsent.personalization_enabled)) resetConversation();
    currentConsent = consent;
    if (providerToggle) providerToggle.checked = consent.provider_enabled;
    if (personalizationToggle) personalizationToggle.checked = consent.personalization_enabled;
    if (providerStatus) providerStatus.textContent = providerVersion !== consent.provider_version ? "説明が更新されています。ページを再読み込みしてください。同意の解除はこの画面からもできます。" : (consent.provider_enabled ? "送信を許可しています" : "利用するには送信への同意が必要です");
    if (personalizationStatus) personalizationStatus.textContent = personalizationVersion !== consent.personalization_version ? "説明が更新されています。ページを再読み込みしてください。" : (consent.personalization_enabled ? "利用中" : "利用しない");
    updateControls();
    return true;
  };
  const applyQuota = (quota) => {
    currentQuota = quota;
    if (quotaStatus) quotaStatus.textContent = `1人1日3回・サイト全体1日50回。${quota.message}`;
    updateControls();
    window.clearTimeout(resetTimer);
    const delay = Date.parse(quota.resets_at) - Date.now();
    if (delay > 0 && delay < 86400000) resetTimer = window.setTimeout(() => { if (active()) refreshQuota(); }, delay + 1000);
  };
  const refreshQuota = (revalidateWhileBusy = false) => {
    if (!active() || quotaLoading || ((submitting || consentUpdating) && !revalidateWhileBusy)) return;
    quotaLoading = true;
    const epoch = contextEpoch;
    updateControls();
    fetch(root.dataset.quotaEndpoint, { headers: { "Accept": "application/json" }, cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw new Error("status unavailable");
        return response.json();
      })
      .then((data) => {
        if (!active() || epoch !== contextEpoch) return;
        if (!data.quota || !validConsent(data.consent)) throw new Error("status unavailable");
        if (applyConsent(data.consent)) {
          revalidationRequired = false;
          applyQuota(data.quota);
        }
      })
      .catch(() => {
        if (!active() || epoch !== contextEpoch) return;
        revalidationRequired = false;
        currentConsent = null;
        resetConversation();
        applyQuota({ available: false, message: "利用回数と同意状態を確認できませんでした。AI相談を閉じて開き直すと再確認できます。施設検索・Q&Aは通常どおり使えます。" });
      })
      .finally(() => {
        quotaLoading = false;
        if (!active()) return;
        updateControls();
        if (revalidationRequired && document.visibilityState === "visible") refreshQuota(true);
      });
  };
  const changeConsent = (kind, enabled) => {
    if (!currentConsent || submitting || quotaLoading || consentUpdating || !active()) return;
    const previous = currentConsent;
    const version = kind === "provider" ? providerVersion : personalizationVersion;
    if (enabled && version !== (kind === "provider" ? previous.provider_version : previous.personalization_version)) return;
    consentUpdating = true;
    resetConversation();
    const epoch = contextEpoch;
    updateControls();
    const status = kind === "provider" ? providerStatus : personalizationStatus;
    if (status) status.textContent = "更新中";
    fetch(kind === "provider" ? root.dataset.providerConsentEndpoint : root.dataset.personalizationEndpoint, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken(), "Accept": "application/json" },
      body: JSON.stringify({ enabled, version, expected_revision: previous.revision })
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!active() || epoch !== contextEpoch) return;
        if (!validConsent(data.consent)) throw new Error("consent unavailable");
        applyConsent(data.consent);
        if (!ok) {
          if (status) status.textContent = typeof data.error === "string" ? data.error : "設定を更新できませんでした。状態を確認してからもう一度変更してください。";
          return;
        }
      })
      .catch(() => {
        if (!active() || epoch !== contextEpoch) return;
        currentConsent = null;
        if (status) status.textContent = "更新結果を確認できませんでした。AI相談を閉じて開き直してください。";
      })
      .finally(() => {
        consentUpdating = false;
        if (!active()) return;
        updateControls();
        if (revalidationRequired && document.visibilityState === "visible") refreshQuota(true);
      });
  };
  document.querySelectorAll("[data-ai-chat-toggle]").forEach((button) => {
    if (button.dataset.aiToggleBound === "true") return;
    button.dataset.aiToggleBound = "true";
    button.addEventListener("click", () => {
      root.classList.toggle("is-open");
      updateViewportHeight();
      if (root.classList.contains("is-open")) {
        refreshQuota();
        if (!isSmallScreen() && !input.disabled) input.focus();
      }
    });
  });
  root.querySelector("[data-ai-chat-close]").addEventListener("click", () => root.classList.remove("is-open"));
  sampleButtons.forEach((button) => button.addEventListener("click", () => {
    if (button.disabled || submitting) return;
    input.value = button.dataset.aiChatSample;
    form.requestSubmit();
  }));
  if (providerToggle) providerToggle.addEventListener("change", () => changeConsent("provider", providerToggle.checked));
  if (personalizationToggle) personalizationToggle.addEventListener("change", () => changeConsent("personalization", personalizationToggle.checked));
  if (petSelect) petSelect.addEventListener("change", resetConversation);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const value = input.value.trim();
    if (!value || submitting || quotaLoading || consentUpdating || !currentQuota || !currentQuota.available ||
        !currentConsent || !currentConsent.provider_enabled || providerVersion !== currentConsent.provider_version || !active()) return;
    const petId = petSelect && !petSelect.disabled ? petSelect.value : null;
    submitting = true;
    updateControls();
    const revision = currentConsent.revision;
    const epoch = contextEpoch;
    appendMessage(messages, value, "user");
    input.value = "";
    appendMessage(messages, "調べています...", "bot");
    const loadingNode = messages.lastElementChild;
    fetch(root.dataset.endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken(), "Accept": "application/json" },
      body: JSON.stringify({ message: value, history_token: historyToken, consent_revision: revision, pet_id: petId })
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!active() || epoch !== contextEpoch || !currentConsent || revision !== currentConsent.revision) return;
        if (!validConsent(data.consent)) throw new Error("invalid chat response");
        if (!applyConsent(data.consent)) return;
        if (!ok) {
          resetConversation();
          appendMessage(messages, typeof data.error === "string" ? data.error : "送信できませんでした。同意状態を確認してください。", "bot");
          currentQuota = null;
          return;
        }
        if (typeof data.reply !== "string") throw new Error("invalid chat response");
        if (data.consent.revision !== revision) return;
        if (data.quota) applyQuota(data.quota);
        if (data.history_reset) resetConversation();
        loadingNode.remove();
        if (currentConsent.provider_enabled) historyToken = typeof data.history_token === "string" ? data.history_token : null;
        appendMessage(messages, data.reply, "bot", data.links, data.disclaimer, data.personalization);
      })
      .catch(() => {
        if (!active() || epoch !== contextEpoch) return;
        loadingNode.remove();
        appendMessage(messages, "すみません、返答を確認できませんでした。サーバーで受け付け済みの場合は1回分に数えられます。残り回数と同意状態を確認してから再度お試しください。", "bot");
        historyToken = null;
        currentQuota = null;
      })
      .finally(() => {
        submitting = false;
        if (!active()) return;
        updateControls();
        if (!currentQuota) refreshQuota();
      });
  });
  const dispose = () => {
    if (!document.contains(root)) return;
    resetConversation();
    currentConsent = null;
    currentQuota = null;
    input.value = "";
    disposed = true;
    window.clearTimeout(resetTimer);
    root.dataset.initialized = "false";
    document.querySelectorAll("[data-ai-chat-toggle]").forEach((button) => { delete button.dataset.aiToggleBound; });
    updateControls();
  };
  document.addEventListener("turbolinks:before-cache", dispose);
  window.addEventListener("pagehide", dispose);
  window.addEventListener("pageshow", (event) => {
    if (event.persisted && !accountChanged) { disposed = false; root.dataset.initialized = "true"; refreshQuota(); }
  });
  document.addEventListener("visibilitychange", () => {
    if (!active()) return;
    if (document.visibilityState !== "visible") {
      // A different tab can change the shared login while this page is hidden.
      resetConversation();
      input.value = "";
      currentConsent = null;
      currentQuota = null;
      revalidationRequired = true;
      updateControls();
    } else if (root.classList.contains("is-open")) {
      if (submitting || consentUpdating) {
        resetConversation();
        currentConsent = null;
        currentQuota = null;
        revalidationRequired = true;
      }
      refreshQuota(true);
    }
  });
  panel.addEventListener("click", (event) => event.stopPropagation());
  updateControls();
  updateViewportHeight();
  window.addEventListener("resize", updateViewportHeight);
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", updateViewportHeight);
    window.visualViewport.addEventListener("scroll", updateViewportHeight);
  }
}
document.addEventListener("turbolinks:load", setupPetterAiChat);
document.addEventListener("DOMContentLoaded", setupPetterAiChat);
